import joi from 'joi';
import { CurrencyBalanceAttributes, TransactionType } from '../../profile-api-client';
import { EventName, EventSource } from '../../api-client/events';

export const noTotalUpdates = {
  totalAccumulated: false,
  totalOverCurrencyPeriod: false,
  totalOverLevelPeriod: false,
};
export enum RuleType {
  EARN = 'EARN',
  BURN = 'BURN',
  ACCESS = 'ACCESS',
}

export enum ActionTypes {
  TRANSACTION = 'TRANSACTION',
  REWARD_ATTRIBUTION = 'REWARD_ATTRIBUTION',
  CAMPAIGN_CODE = 'CAMPAIGN_CODE',
  STOP = 'STOP',
  UPDATE_LEVEL = 'UPDATE_LEVEL',
  ACCESS = 'ACCESS',
}

export enum ImpactMode {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  VARIABLE_AMOUNT = 'VARIABLE_AMOUNT',
}

export enum RewardListRuleState {
  AVAILABLE = 'AVAILABLE',
  NOT_ENOUGH_FUNDS = 'NOT_ENOUGH_FUNDS',
  SOLD_OUT = 'SOLD_OUT',
}

export enum AccessImpactMode {
  GRANT_ACCESS = 'GRANT_ACCESS',
  REVOKE_ACCESS = 'REVOKE_ACCESS',
}

export enum ActionCondition {
  HIT = 'HIT',
  MISS = 'MISS',
}

export interface ObtainCampaignCodeAction {
  kind: ActionTypes.CAMPAIGN_CODE;
  impact: {
    promocode: string;
    campaignId: string;
    giftId?: number;
  };
}

export interface ObtainRewardAction {
  kind: ActionTypes.REWARD_ATTRIBUTION;
  impact: {
    rewardId: string;
  };
}

export type RewardAction = ObtainRewardAction | ObtainCampaignCodeAction;

export interface StopAction {
  kind: ActionTypes.STOP;
}

export interface GrantAccessImpact {
  mode: AccessImpactMode.GRANT_ACCESS;
}

export interface RevokeAccessImpact {
  mode: AccessImpactMode.REVOKE_ACCESS;
}

export interface AccessAction {
  kind: ActionTypes.ACCESS;
  actionCondition: ActionCondition;
  impact: GrantAccessImpact | RevokeAccessImpact;
}

export interface TransactionFixedImpact {
  mode: ImpactMode.FIXED_AMOUNT;
  currency: string;
  value: number;
}

export interface TransactionVariousImpact {
  mode: ImpactMode.VARIABLE_AMOUNT;
  currency: string;
  factorField: FactorField;
  factor: number;
}

export type TransactionImpact = TransactionFixedImpact | TransactionVariousImpact;

export interface UpdateTotals {
  totalAccumulated?: boolean;
  totalOverLevelPeriod?: boolean;
  totalOverCurrencyPeriod?: boolean;
}

export const $updateTotals = joi
  .object<UpdateTotals>({
    totalAccumulated: joi.boolean().optional().default(true),
    totalOverLevelPeriod: joi.boolean().optional().default(true),
    totalOverCurrencyPeriod: joi.boolean().optional().default(true),
  })
  .optional()
  .default({
    totalAccumulated: true,
    totalOverLevelPeriod: true,
    totalOverCurrencyPeriod: true,
  });

export interface TransactionAction {
  kind: ActionTypes.TRANSACTION;
  impact: TransactionImpact;
  updateTotals?: UpdateTotals;
}

export interface EarningTransactionAction {
  kind: ActionTypes.TRANSACTION;
  impact: EarningRuleActionTransImpact;
}

export interface EarningRuleActionTransImpact {
  mode: ImpactMode;
  value: number | null;
  currency: string;
  factorField?: string;
  factor?: number;
}

export type Action = RewardAction | TransactionAction | StopAction | AccessAction;

export interface RuleI18nAttributes {
  [key: string]: {
    description: string;
    redirectUrl?: string;
  };
}

export interface AccessRuleI18nAttributes {
  [key: string]: {
    redirectUrl: string;
    description: string;
  };
}

export interface ImpactRule {
  id?: string;
  type: TransactionType;
  accountId: string;
  programId: string;
  currency?: {
    id: string;
    name?: string;
    value: number;
  };
  state?: RewardListRuleState;
  quantity?: number;
  actions: Action[];
  i18nAttributes: RuleI18nAttributes;
  reward?: {
    id: string;
  };
}

export interface AccessRule {
  id?: string;
  accountId?: string;
  programId: string;
  actions: AccessAction[];
  i18nAttributes: AccessRuleI18nAttributes;
}

export enum PeriodicityLimitationEnum {
  FOREVER = 'FOREVER', // can never be re-executed
  HOURS_24 = 'HOURS_24', // can be re-executed 24 hours after the last time
  DAYS_7 = 'DAYS_7', // can be re-executed 7 days after the last time
  DAYS_30 = 'DAYS_30', // can be re-executed 30 days after the last time
  DAYS_365 = 'DAYS_365', // can be re-executed 365 days after the last time
  DAILY = 'DAILY', // can be re-executed at the start of the next day
  WEEKLY = 'WEEKLY', // can be re-executed at the start of the next week
  MONTHLY = 'MONTHLY', // can be re-executed at the start of the next month
  YEARLY = 'YEARLY', // can be re-executed at the start of the next year
}

export interface PeriodicityLimitation {
  enabled: boolean;
  periodicity: PeriodicityLimitationEnum;
  occurrence: number;
  timezone: string;
}

export interface RuleOptions {
  periodicityLimitation?: PeriodicityLimitation;
}

export interface RuleInput {
  legacyAccountId?: string; // used only for frontend purpose, stripped by backend routes
  name: string;
  eventSource: EventSource;
  eventName: EventName;
  type: RuleType;
  i18nAttributes: RuleI18nAttributes;
  criteria: Record<string, any> | null | undefined;
  actions: Action[];
  options: RuleOptions;
  valid?: Record<string, any>;
  deletedAt?: string;
  position?: number;
  version?: number;
  positionVersion?: number;
}

export interface RuleAttributes extends RuleInput {
  id: string;
  accountId: string;
  programId: string;
  createdAt?: string;
  updatedAt?: string;
  version: number;
  positionVersion: number;
}

export interface AccessRuleAttributes extends RuleAttributes {
  i18nAttributes: AccessRuleI18nAttributes;
}

export type FactorField =
  | QualifioCampaignFactorField
  | QualifioLoyaltyMemberVisitFactorField
  | EcommercePurchaseFactorField
  | string;

export enum QualifioCampaignFactorField {
  DISTANCE = 'DISTANCE',
  POINT = 'POINT',
  TIME = 'TIME',
}

export enum QualifioLoyaltyMemberVisitFactorField {
  CONSECUTIVE_DAILY_VISITS = 'CONSECUTIVE_DAILY_VISITS',
  TOTAL_DAILY_VISITS = 'TOTAL_DAILY_VISITS',
}

export enum QualifioLoyaltyBirthdayFactorField {
  AGE = 'AGE',
}

export enum EcommercePurchaseFactorField {
  AMOUNT_VAT_EXCLUDED = 'amountVatExcluded',
  AMOUNT_VAT_INCLUDED = 'amountVatIncluded',
  SHIPPING_COST_VAT_INCLUDED = 'shippingCostVatIncluded',
  TOTAL_AMOUNT_VAT_INCLUDED = 'totalAmountVatIncluded',
  BASKET_TOTAL = 'basketTotal',
  QUANTITY_TOTAL = 'quantityTotal',
}

export type EventReportDetailsProperties =
  | 'id'
  | 'eventSource'
  | 'eventName'
  | 'programId'
  | 'programName'
  | 'clientEventId'
  | 'eventTimestamp'
  | 'eventCreatedAt'
  | 'createdAt'
  | 'eventId'
  | 'status'
  | 'isRepublished';

export interface ProfileCriteria {
  email?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  city?: string;
  birthdate?: string;
  language?: string;
  level?: string;
  currencies?: CurrencyBalanceAttributes[];
  customKeys?: Record<string, any>;
  isTestUser?: boolean;
}

export interface RulePositionChange {
  ruleId: string;
  newPosition: number;
  positionVersion: number;
}

export interface RuleLastModificationInfo {
  userName: string;
  date: string;
}
